/**
 * Feature entitlements — the single place that knows what Lite and Pro include.
 *
 * The brief for this rebuild was explicit that `if (plan === 'pro')` must not
 * end up scattered through hundreds of components, and the reason is worth
 * recording: a scattered check is a pricing decision encoded in a view. When
 * sales moves one feature between editions, every one of those call sites is a
 * place the change can be missed, and the failure mode is a customer seeing
 * something they did not buy — or worse, not seeing something they did.
 *
 * So the edition is resolved once, into a flat set of feature flags, and every
 * caller asks about a *feature* rather than an edition. Nothing outside this
 * file compares against 'lite' or 'pro'.
 *
 * Two further rules matter:
 *
 *   - An entitlement is not a permission. Entitlements answer "did this school
 *     buy this?"; permissions answer "may this user do this?". Both are checked
 *     on every protected route, and neither substitutes for the other.
 *
 *   - Lite must not feel crippled. The split below gives Lite everything a
 *     small school needs to actually run — enrol, schedule, take attendance,
 *     grade, report, communicate — and reserves for Pro the things that only
 *     matter at district scale or with specialist staff.
 */

export const EDITIONS = ['lite', 'pro'] as const
export type Edition = (typeof EDITIONS)[number]

/**
 * Every gateable capability. Grouped by the area it belongs to, which keeps the
 * list legible as it grows and makes a missing feature obvious in review.
 */
export const FEATURES = [
  // ── Core school operations (Lite and Pro) ─────────────────────────────────
  'students.manage',
  'guardians.households',
  'staff.manage',
  'enrollment.basic',
  'academics.courses',
  'academics.sections',
  'academics.terms',
  'calendar.school',
  'schedule.student',
  'schedule.teacher',
  'attendance.daily',
  'attendance.period',
  'gradebook.core',
  'gradebook.categories.weighted',
  'grades.overrides',
  'reports.progressReport',
  'reports.reportCard',
  'reports.transcript.basic',
  'portal.parent',
  'portal.student',
  'portal.staff',
  'communications.basic',
  'documents.storage',
  'discipline.basic',
  'health.alerts',
  'reports.center',
  'reports.print',
  'export.csv',
  'security.roles',
  'branding.school',
  'audit.history',

  // ── Multi-school and district (Pro) ───────────────────────────────────────
  'district.multiSchool',
  'district.reporting',
  'district.comparativeAnalytics',

  // ── Advanced scheduling (Pro) ─────────────────────────────────────────────
  'scheduling.master',
  'scheduling.conflictDetection',
  'scheduling.automated',
  'scheduling.courseRequests',

  // ── Advanced academics (Pro) ──────────────────────────────────────────────
  'graduation.tracking',
  'graduation.creditAudit',
  'gpa.weighted',
  'gpa.classRank',
  'transcript.official',
  'grading.standardsBased',
  'grading.rubrics',
  'grading.competencies',
  'analytics.gradeDistribution',
  'analytics.studentSuccess',
  'analytics.academicInsights',

  // ── Student services (Pro) ────────────────────────────────────────────────
  'interventions.mtss',
  'discipline.advanced',
  'discipline.behaviorPlans',
  'health.office',
  'health.medicationAdministration',
  'health.immunizations',
  'services.iep',

  // ── Operations (Pro) ──────────────────────────────────────────────────────
  'operations.transportation',
  'operations.cafeteria',
  'fees.studentAccounts',
  'fees.payments',
  'activities.athleticEligibility',

  // ── Advanced platform (Pro) ───────────────────────────────────────────────
  'assignments.onlineSubmission',
  'communications.advanced',
  'communications.automatedAlerts',
  'alerts.academicThresholds',
  'customFields',
  'reports.customBuilder',
  'reports.scheduled',
  'documents.workflows',
  'documents.digitalSignature',
  'workflow.approvals',
  'integrations.api',
  'integrations.sso',
  'data.import',
  'data.exportAdvanced',
  'guardians.advancedPermissions',
  'security.advancedPermissions',
  'staff.evaluations',
  'helper.assistant',

  // ── Focus group and Feedback Lab ──────────────────────────────────────────
  //
  // Granted per tenant, not per edition: the Feedback Lab belongs to the focus
  // group programme rather than to anything a school buys, and is switched on
  // for the tester tenant through `featureOverrides`. Listing them here rather
  // than inventing a parallel flag system keeps one answer to "may this
  // account do this", which was the brief's first architectural instruction.
  'feedback.lab',
  'feedback.voting',
  'feedback.bugReports',
  'feedback.screenshots',
  'feedback.missions',
  'feedback.betaFeatures',
  'feedback.announcements',
] as const

export type Feature = (typeof FEATURES)[number]

/**
 * Features included in Cornerstone Education Lite.
 *
 * Everything a single school needs to operate end to end. A school on Lite can
 * enrol a student, schedule them, take attendance, run a gradebook, issue a
 * report card and talk to the family — without being told to upgrade.
 */
const LITE_FEATURES: readonly Feature[] = [
  'students.manage',
  'guardians.households',
  'staff.manage',
  'enrollment.basic',
  'academics.courses',
  'academics.sections',
  'academics.terms',
  'calendar.school',
  'schedule.student',
  'schedule.teacher',
  'attendance.daily',
  'attendance.period',
  'gradebook.core',
  'gradebook.categories.weighted',
  'grades.overrides',
  'reports.progressReport',
  'reports.reportCard',
  'reports.transcript.basic',
  'portal.parent',
  'portal.student',
  'portal.staff',
  'communications.basic',
  'documents.storage',
  'discipline.basic',
  'health.alerts',
  'reports.center',
  'reports.print',
  'export.csv',
  'security.roles',
  'branding.school',
  'audit.history',
]

/**
 * The focus group programme, which is not something a school buys.
 *
 * Excluded from both editions and granted per tenant through
 * `featureOverrides`. Without this, `pro: new Set(FEATURES)` would hand the
 * Feedback Lab to every Pro customer the day the first one signs — a
 * tester-facing surface appearing in a paying school's navigation.
 */
const PROGRAMME_FEATURES: readonly Feature[] = [
  'feedback.lab',
  'feedback.voting',
  'feedback.bugReports',
  'feedback.screenshots',
  'feedback.missions',
  'feedback.betaFeatures',
  'feedback.announcements',
]

/** Pro is a superset: everything in Lite, plus every advanced capability. */
const EDITION_FEATURES: Record<Edition, ReadonlySet<Feature>> = {
  lite: new Set(LITE_FEATURES),
  pro: new Set(FEATURES.filter((feature) => !PROGRAMME_FEATURES.includes(feature))),
}

export { PROGRAMME_FEATURES }

/**
 * A tenant's commercial position, as stored on the tenant document.
 *
 * `featureOverrides` exists for the cases a pure edition cannot express: a Lite
 * school that bought transportation as an add-on, or a Pro trial with the
 * assistant switched off pending a data-processing agreement. Overrides are
 * explicit and auditable rather than a second pricing tier nobody can find.
 */
export interface SubscriptionState {
  edition: Edition
  status: 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled'
  /** ISO date. A trial or a cancelled subscription stops entitling on this date. */
  expiresAt?: string | null
  featureOverrides?: Partial<Record<Feature, boolean>>
}

export interface EntitlementContext {
  /** Evaluation time, injectable so expiry logic is testable. */
  now?: Date
}

/**
 * The resolved answer for one tenant: a frozen set of features plus the reason
 * behind it. Built once per request and carried on the request context.
 */
export interface Entitlements {
  edition: Edition
  /** False when the subscription has lapsed — see `resolveEntitlements`. */
  active: boolean
  features: ReadonlySet<Feature>
  has(feature: Feature): boolean
  /** Explains an answer in words a support agent can use. */
  explain(feature: Feature): string
}

/**
 * Suspended and cancelled tenants keep read access to the core record set.
 *
 * A school that misses an invoice must not lose the ability to produce a
 * transcript for a student applying to college. Billing enforcement belongs in
 * billing; it is not a reason to make a child's academic record unreachable.
 */
const GRACE_FEATURES: readonly Feature[] = [
  'students.manage',
  'reports.transcript.basic',
  'reports.reportCard',
  'reports.print',
  'export.csv',
  'security.roles',
  'audit.history',
]

/**
 * Returns a Set whose mutators throw.
 *
 * `Object.freeze` is not enough here: a Set's contents live in internal slots
 * rather than own properties, so a frozen Set still accepts `.add()`. Since
 * this set is the answer to "what did this school pay for", a caller being
 * able to quietly add to it is the whole problem the central service exists to
 * prevent. Defining the mutators as own properties shadows the prototype's.
 */
function sealFeatureSet(source: Set<Feature>): ReadonlySet<Feature> {
  const view = new Set(source)
  const refuse = () => {
    throw new TypeError('Entitlements are immutable. Change the subscription or a feature override instead.')
  }
  Object.defineProperties(view, {
    add: { value: refuse, configurable: false, writable: false },
    delete: { value: refuse, configurable: false, writable: false },
    clear: { value: refuse, configurable: false, writable: false },
  })
  return Object.freeze(view)
}

export function resolveEntitlements(
  subscription: SubscriptionState,
  context: EntitlementContext = {},
): Entitlements {
  const now = context.now ?? new Date()
  const edition = EDITIONS.includes(subscription.edition) ? subscription.edition : 'lite'

  const expired =
    subscription.expiresAt != null &&
    subscription.expiresAt !== '' &&
    new Date(subscription.expiresAt).getTime() <= now.getTime()

  const lapsed =
    subscription.status === 'suspended' ||
    subscription.status === 'cancelled' ||
    ((subscription.status === 'trial' || subscription.status === 'active') && expired)

  const base = lapsed ? new Set<Feature>(GRACE_FEATURES) : new Set<Feature>(EDITION_FEATURES[edition])

  // Overrides apply after the edition, and after the lapse reduction, so an
  // add-on cannot resurrect a suspended tenant's full feature set.
  const overrides = subscription.featureOverrides ?? {}
  for (const [feature, enabled] of Object.entries(overrides) as [Feature, boolean][]) {
    if (!FEATURES.includes(feature)) continue
    if (enabled && !lapsed) base.add(feature)
    if (!enabled) base.delete(feature)
  }

  const features = sealFeatureSet(base)

  return {
    edition,
    active: !lapsed,
    features,
    has: (feature: Feature) => features.has(feature),
    explain: (feature: Feature) => {
      if (features.has(feature)) {
        if (overrides[feature] === true) return `Enabled for this school as an add-on to ${editionLabel(edition)}.`
        return `Included in ${editionLabel(edition)}.`
      }
      if (overrides[feature] === false) return 'Switched off for this school by an administrator.'
      if (lapsed) {
        return subscription.status === 'suspended' || subscription.status === 'cancelled'
          ? `Unavailable while this subscription is ${subscription.status}. Core student records stay accessible.`
          : 'Unavailable because this subscription has expired. Core student records stay accessible.'
      }
      if (EDITION_FEATURES.pro.has(feature)) return 'Available in Cornerstone Education Pro.'
      return 'Not available on this plan.'
    },
  }
}

export function editionLabel(edition: Edition): string {
  return edition === 'pro' ? 'Cornerstone Education Pro' : 'Cornerstone Education Lite'
}

/** True when a feature exists only in Pro — drives the upgrade prompts. */
export function isProOnly(feature: Feature): boolean {
  return !EDITION_FEATURES.lite.has(feature) && EDITION_FEATURES.pro.has(feature)
}

/** Lite's feature list, for the pricing page and the edition comparison. */
export function featuresForEdition(edition: Edition): Feature[] {
  return [...EDITION_FEATURES[edition]]
}
